import React from 'react';
import { DefaultSeo } from 'next-seo';
import LocaleContext from 'lib/locale-context';
import lexemes from 'components/lexemes';
import supportedLanguageIds, { SupportedLanguage } from 'lib/lexemes/supported-languages';
import Head from 'next/head';
import { setupProperPartnerId } from 'lib/handle-data';

const PAGE_PARTNER_NAME = 'Badoo';
const LEXEME_PARAMS = {
    page_partner_name: PAGE_PARTNER_NAME,
};

type SeoKeyValueStorage = Record<number, (params: { page_partner_name: string }) => string>;

/**
 * Warning, some markets has different set of articles,
 * make sure to check for each Article on Each market
 */
const pageTitles: SeoKeyValueStorage = {
    3513: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMeetingInRealLife,
    3519: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMeetingInRealLifeArticleTwo,
    3697: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMeetingInRealLifeArticleThree,
    3687: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMeetingInRealLifeArticleThree,
    3477: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionHarmfulBehaviour,
    3495: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionHarmfulBehaviourArticleTwo,
    3501: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionHarmfulBehaviourArticleThree,
    3507: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionHarmfulBehaviourArticleFourth,
    3649: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionHarmfulBehaviourArticleFifth,
    3577: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionSafetyTips,
    3571: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionSafetyTipsArticleTwo,
    3559: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionSafetyTipsArticleThree,
    3565: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionSafetyTipsArticleFourth,
    3537: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionSafetyTipsArticleFifth,
    3583: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMentalWellbeing,
    3589: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMentalWellbeingArticleTwo,
    3607: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMentalWellbeingArticleThree,
    3595: lexemes.seoPageTitleList.lxSeoPageTitleGuidesSectionMentalWellbeingArticleFourth,
};

const pageDescriptions: SeoKeyValueStorage = {
    3513: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionMeetingInRealLife,
    3519: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionMeetingInRealLifeArticleTwo,
    3697: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionMeetingInRealLifeArticleThree,
    3687: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionMeetingInRealLifeArticleThree,
    3477: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionHarmfulBehaviour,
    3495: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionHarmfulBehaviourArticleTwo,
    3501: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionHarmfulBehaviourArticleThree,
    3507: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionHarmfulBehaviourArticleFourth,
    3649: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionHarmfulBehaviourArticleFifth,
    3577: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionSafetyTips,
    3571: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionSafetyTipsArticleTwo,
    3559: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionSafetyTipsArticleThree,
    3565: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionSafetyTipsArticleFourth,
    3537: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionSafetyTipsArticleFifth,
    3583: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionMentalWellbeing,
    3589: lexemes.seoPageDescriptionList.lxSeoPageDescriptionGuidesSectionMentalWellbeingArticleTwo,
    3607: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionMentalWellbeingArticleThree,
    3595: lexemes.seoPageDescriptionList
        .lxSeoPageDescriptionGuidesSectionMentalWellbeingArticleFourth,
};

/**
 * fix issue with trailing slash on pre-render from server
 */
function setProperUrlForServerRender(url: string) {
    return url.replace('safety-centre//social', 'safety-centre/social');
}

const setSEOImages = (domain: string, lang: string, alt: string) => {
    return [
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/00.jpg`),
            width: 1200,
            height: 630,
            alt,
        },
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/01.jpg`),
            width: 681,
            height: 368,
            alt,
        },
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/02.jpg`),
            width: 458,
            height: 240,
            alt,
        },
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/03.jpg`),
            width: 438,
            height: 240,
            alt,
        },
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/04.jpg`),
            width: 520,
            height: 270,
            alt,
        },
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/05.jpg`),
            width: 264,
            height: 143,
            alt,
        },
        {
            type: 'image/jpeg',
            url: setProperUrlForServerRender(`${domain}/social/${lang}/06.jpg`),
            width: 90,
            height: 90,
            alt,
        },
    ];
};

const SEO = {
    URL: 'https://badoo.com/safety-centre',
    get TITLE() {
        return setupProperPartnerId(lexemes.seoPageTitleList.lxSeoPageTitleIndex(LEXEME_PARAMS));
    },
    get DESCRIPTION() {
        return setupProperPartnerId(
            lexemes.seoPageDescriptionList.lxSeoPageDescriptionIndex(LEXEME_PARAMS)
        );
    },
    FACEBOOK_APP_ID: '107433747809',
    TWITTER_APP_ID: '351331194',
    TWITTER_ACCOUNT: '@Badoo',
    get IMAGE_ALT() {
        return setupProperPartnerId(lexemes.seoPageTitleList.lxSeoPageTitleIndex(LEXEME_PARAMS));
    },
};

/**
 * Returns "alternate" languages for SEO
 */
function getLanguageAlternates(domain: string, pageSection?: number, pageId?: number) {
    if (pageSection && pageId) {
        return supportedLanguageIds.map((lang) => {
            return {
                hrefLang: lang,
                // /${lang}/guides?section=${searchResult.page_id}&entry=${searchResult.help_entry_id}
                href: `${domain}/${lang}/guides?section=${pageSection}&entry=${pageId}`,
            };
        });
    }

    return supportedLanguageIds.map((lang) => {
        return {
            hrefLang: lang,
            href: `${domain}/${lang}/`,
        };
    });
}

function getCanonical(
    domain: string,
    lang: SupportedLanguage,
    pageSection?: number,
    pageId?: number
) {
    if (pageSection && pageId) {
        return `${domain}/${lang}/guides?section=${pageSection}&entry=${pageId}`;
    }

    return `${domain}/`;
}

export type PageSeoPropsType = {
    pageId?: number;
    pageSection?: number;
};

const PageSeo = (props: PageSeoPropsType) => {
    const domain = global.window
        ? `${global.window.location.origin}/safety-centre`
        : 'https://badoo.com/safety-centre';
    const languageAlternates = getLanguageAlternates(domain, props.pageSection, props.pageId);
    const lang = React.useContext(LocaleContext) as SupportedLanguage;
    const canonicalUrl = getCanonical(domain, lang, props.pageSection, props.pageId);
    const imageAlt = SEO.IMAGE_ALT;
    const pageTitleCall = props.pageId && pageTitles[props.pageId];
    const pageTitle =
        props.pageId && typeof pageTitleCall === 'function'
            ? setupProperPartnerId(pageTitleCall(LEXEME_PARAMS))
            : SEO.TITLE;
    const pageDescriptionCall = props.pageId && pageDescriptions[props.pageId];
    const pageDescriotion =
        props.pageId && typeof pageDescriptionCall === 'function'
            ? setupProperPartnerId(pageDescriptionCall(LEXEME_PARAMS))
            : SEO.DESCRIPTION;

    return (
        <>
            <DefaultSeo
                title={pageTitle}
                description={pageDescriotion}
                languageAlternates={languageAlternates}
                canonical={canonicalUrl}
                facebook={{
                    appId: SEO.FACEBOOK_APP_ID,
                }}
                additionalMetaTags={[
                    {
                        property: 'twitter:app:name:iphone',
                        content: SEO.TITLE,
                    },
                    {
                        property: 'twitter:app:id:iphone',
                        content: SEO.TWITTER_APP_ID,
                    },
                    {
                        property: 'twitter:app:name:ipad',
                        content: SEO.TITLE,
                    },
                    {
                        property: 'twitter:app:id:ipad',
                        content: SEO.TWITTER_APP_ID,
                    },
                ]}
                openGraph={{
                    type: 'website',
                    locale: lang,
                    url: canonicalUrl,
                    site_name: SEO.TITLE,
                    images: setSEOImages(domain, lang, imageAlt),
                }}
                twitter={{
                    handle: SEO.TWITTER_ACCOUNT,
                    cardType: 'summary_large_image',
                    site: SEO.TWITTER_ACCOUNT,
                }}
            />
            <Head>
                {/* https://github.com/garmeeh/next-seo/issues/14 */}
                <meta name="twitter:image" content={setSEOImages(domain, lang, imageAlt)[4].url} />
            </Head>
        </>
    );
};

export default PageSeo;
